import {
  forwardRef,
  HTMLAttributes,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { MessageContent, useMessageContext } from "./message"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import {
  AlertCircle,
  AudioLines,
  Download,
  ExternalLink,
  Loader2,
  Maximize,
  MoreVertical,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { MessageText } from "@/components/ui/message/message-text"
import useHandleDownload from "@/hooks/useHandleDownload"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Slider } from "@/components/ui/slider"
import useDownloadFileName from "@/hooks/useDownloadFileName"
import useMessage from "@/components/ui/message/useMessage"
import { isMobile } from "@/utils/detectDevice"
import { useAudioPlayback } from "@/contexts/AudioPlaybackContext"

// Module-level waveform cache to avoid re-decoding audio files
const waveformCache = new Map<string, number[]>()

export interface MessageVideoProps extends HTMLAttributes<HTMLDivElement> {
  src?: string
  poster?: string
  title?: string
  duration?: number
  width?: number
  height?: number
  autoPlay?: boolean
  controls?: boolean
  muted?: boolean
  loop?: boolean
  onDownload?: () => void
  onExternalView?: () => void
  showDownload?: boolean
  aspectRatio?: "video" | "square" | "auto"
  forwarded?: boolean
  reply?: boolean
}

export interface MessageAudioProps extends HTMLAttributes<HTMLDivElement> {
  src?: string
  duration?: number
  transcript?: string
  autoPlay?: boolean
  onDownload?: () => void
  onExternalView?: () => void
  showDownload?: boolean
  showWaveform?: boolean
  forwarded?: boolean
  reply?: boolean
}

/** A MediaRecorder WebM blob carries no duration in its EBML header, so the
 * media element reports `Infinity` until the stream has been played to the end,
 * and `NaN` when the file failed to load. Neither is a length we can print or
 * seek within, so every consumer asks first. */
const isKnownDuration = (duration: number) =>
  Number.isFinite(duration) && duration > 0

const formatTime = (time: number) => {
  if (!Number.isFinite(time)) return "--:--"
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

/** Fraction of the track already played, 0 while the length is still unknown. */
const playedFraction = (currentTime: number, duration: number) =>
  isKnownDuration(duration) ? currentTime / duration : 0

const MessageVideo = forwardRef<HTMLDivElement, MessageVideoProps>(
  (
    {
      className,
      src,
      poster,
      duration,
      width,
      height,
      autoPlay = false,
      controls = true,
      muted = false,
      loop = false,
      onDownload,
      onExternalView,
      showDownload = true,
      aspectRatio = "video",
      forwarded = false,
      reply = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const videoRef = useRef<HTMLVideoElement>(null)
    const progressRef = useRef<HTMLDivElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [videoDuration, setVideoDuration] = useState(duration || 0)
    const [isMuted, setIsMuted] = useState(muted)
    const [volume, setVolume] = useState(1)
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [volumeOpen, setVolumeOpen] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const download = useHandleDownload()
    const {
      message: originalMessage,
      replyMessage,
      actionButtonsVisible,
    } = useMessageContext()
    const message =
      forwarded && originalMessage.forwarded
        ? originalMessage.forwarded
        : reply && replyMessage
          ? replyMessage
          : originalMessage

    const url = src ? src : message.url

    const downloadFileName = useDownloadFileName(message)
    useEffect(() => {
      const video = videoRef.current
      if (!video) return

      const handleTimeUpdate = () => setCurrentTime(video.currentTime)
      const handleDurationChange = () => setVideoDuration(video.duration)
      const handlePlay = () => setIsPlaying(true)
      const handlePause = () => setIsPlaying(false)
      const handleCanPlay = () => setIsLoading(false)
      const handleError = () => {
        setIsLoading(false)
        setHasError(true)
      }

      video.addEventListener("timeupdate", handleTimeUpdate)
      video.addEventListener("durationchange", handleDurationChange)
      video.addEventListener("play", handlePlay)
      video.addEventListener("pause", handlePause)
      video.addEventListener("canplay", handleCanPlay)
      video.addEventListener("error", handleError)

      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate)
        video.removeEventListener("durationchange", handleDurationChange)
        video.removeEventListener("play", handlePlay)
        video.removeEventListener("pause", handlePause)
        video.removeEventListener("canplay", handleCanPlay)
        video.removeEventListener("error", handleError)
      }
    }, [])

    // Fullscreen event listener
    useEffect(() => {
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement)
      }

      document.addEventListener("fullscreenchange", handleFullscreenChange)
      return () => {
        document.removeEventListener("fullscreenchange", handleFullscreenChange)
      }
    }, [])

    const togglePlayPause = () => {
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause()
        } else {
          videoRef.current.play()
        }
      }
    }

    const handleFullscreen = async () => {
      if (!videoRef.current) return

      try {
        if (isFullscreen) {
          await document.exitFullscreen()
        } else {
          await videoRef.current.requestFullscreen()
        }
      } catch (error) {
        console.error("Fullscreen error:", error)
      }
    }

    const handleSeek = (value: number) => {
      if (!isKnownDuration(videoDuration)) return
      if (videoRef.current) {
        videoRef.current.currentTime = (value / 100) * videoDuration
      }
    }

    const handleVolumeChange = (values: number[]) => {
      if (videoRef.current) {
        const value = values[0]
        const newVolume = value / 100
        videoRef.current.volume = newVolume
        setVolume(newVolume)
        setIsMuted(newVolume === 0)
      }
    }
    const toggleMute = () => {
      if (videoRef.current) {
        const newMuted = !isMuted
        videoRef.current.muted = newMuted
        setIsMuted(newMuted)
      }
    }

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!progressRef.current || !isKnownDuration(videoDuration)) return

      const rect = progressRef.current.getBoundingClientRect()
      const percent = ((e.clientX - rect.left) / rect.width) * 100
      handleSeek(Math.max(0, Math.min(100, percent)))
    }

    const getAspectRatioClass = () => {
      switch (aspectRatio) {
        case "video":
          return "aspect-video"
        case "square":
          return "aspect-square"
        default:
          return ""
      }
    }

    const handleDownload = onDownload
      ? onDownload
      : downloadFileName && url
        ? () => {
            download(url, downloadFileName)
          }
        : undefined

    const handleExternalView = () => {
      if (onExternalView) {
        onExternalView()
      } else {
        window.open(url, "_blank")
      }
    }

    return (
      <MessageContent ref={ref} {...props}>
        <div
          className={cn(
            "relative overflow-hidden rounded-lg border bg-secondary",
            // Mobile-first responsive sizing
            "w-full max-w-[280px]", // Mobile default
            "sm:max-w-sm md:max-w-md", // Larger screens
            // Shrink when action buttons are visible on mobile
            isMobile && actionButtonsVisible && "max-w-[240px]",
            className,
          )}
        >
          {/* Video Element */}
          <div className={cn("relative", getAspectRatioClass())}>
            <video
              ref={videoRef}
              src={url}
              poster={poster}
              width={width}
              height={height}
              autoPlay={autoPlay}
              muted={muted}
              loop={loop}
              className="h-full w-full object-cover"
              playsInline
            />

            {/* Loading Spinner */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}

            {/* Error State */}
            {hasError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <AlertCircle className="mx-auto mb-2 h-8 w-8" />
                  <p className="text-sm">{t("chat.media.videoLoadFailed")}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExternalView}
                  >
                    {t("chat.media.tryExternalPlayer")}
                  </Button>
                </div>
              </div>
            )}

            {/* Play/Pause Overlay */}
            {!isLoading && !hasError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-16 w-16 opacity-0 hover:opacity-100"
                  onClick={togglePlayPause}
                >
                  {isPlaying ? (
                    <Pause className="h-8 w-8" />
                  ) : (
                    <Play className="ml-1 h-8 w-8" />
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Custom Controls */}
          {controls && !hasError && (
            <div
              className={cn(
                "flex items-center gap-1 px-2 py-2",
                // Responsive height and spacing
                "h-10 sm:h-12",
                "sm:gap-2 sm:px-3",
                // More compact when action buttons are visible on mobile
                isMobile && actionButtonsVisible && "h-9 gap-1 px-1",
              )}
            >
              {/* Play/Pause Button */}
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "h-8 w-8",
                  // Smaller on mobile when actions are visible
                  isMobile && actionButtonsVisible && "h-6 w-6",
                )}
                onClick={togglePlayPause}
                disabled={hasError}
              >
                {isLoading ? (
                  <Loader2
                    className={cn(
                      "h-4 w-4 animate-spin",
                      isMobile && actionButtonsVisible && "h-3 w-3",
                    )}
                  />
                ) : isPlaying ? (
                  <Pause
                    className={cn(
                      "h-4 w-4",
                      isMobile && actionButtonsVisible && "h-3 w-3",
                    )}
                  />
                ) : (
                  <Play
                    className={cn(
                      "h-4 w-4",
                      isMobile && actionButtonsVisible && "h-3 w-3",
                    )}
                  />
                )}
              </Button>

              {/* Progress Section */}
              <div
                className={cn(
                  "flex flex-1 items-center gap-2",
                  // More compact spacing on mobile when actions are visible
                  isMobile && actionButtonsVisible && "gap-1",
                )}
              >
                <span
                  className={cn(
                    "font-mono text-xs",
                    // Hide time display on very small screens when actions are visible
                    isMobile && actionButtonsVisible && "hidden",
                  )}
                >
                  {formatTime(currentTime)}/{formatTime(videoDuration)}
                </span>

                <div
                  ref={progressRef}
                  className="flex-1 cursor-pointer"
                  onClick={handleProgressClick}
                >
                  <Progress
                    value={playedFraction(currentTime, videoDuration) * 100}
                    className="h-2 bg-base-primary/20 [&>div]:bg-base-primary"
                  />
                </div>
              </div>

              {/* Volume Control */}
              <Popover open={volumeOpen} onOpenChange={setVolumeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-8 w-8",
                      // Smaller on mobile when actions are visible
                      isMobile && actionButtonsVisible && "h-6 w-6",
                    )}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX
                        className={cn(
                          "h-4 w-4",
                          isMobile && actionButtonsVisible && "h-3 w-3",
                        )}
                      />
                    ) : (
                      <Volume2
                        className={cn(
                          "h-4 w-4",
                          isMobile && actionButtonsVisible && "h-3 w-3",
                        )}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="h-auto w-auto p-3"
                  side="top"
                  align="center"
                >
                  <div className="flex w-6 flex-col items-center gap-2">
                    <div className="my-3 flex h-20 items-center justify-center">
                      <Slider
                        orientation="vertical"
                        onValueChange={handleVolumeChange}
                        value={isMuted ? [0] : [volume * 100]}
                        max={100}
                        step={1}
                      />
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-6 whitespace-nowrap px-2 text-xs",
                        isMuted && "bg-muted",
                      )}
                      onClick={toggleMute}
                    >
                      <VolumeX className="h-4 w-4" />
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-8 w-8",
                      // Smaller on mobile when actions are visible
                      isMobile && actionButtonsVisible && "h-6 w-6",
                    )}
                  >
                    <MoreVertical
                      className={cn(
                        "h-4 w-4",
                        isMobile && actionButtonsVisible && "h-3 w-3",
                      )}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleFullscreen}>
                    <Maximize className="mr-2 h-4 w-4" />
                    {isFullscreen
                      ? t("chat.media.exitFullscreen")
                      : t("chat.media.fullScreen")}
                  </DropdownMenuItem>
                  {showDownload && (
                    <DropdownMenuItem onClick={handleDownload}>
                      <Download className="mr-2 h-4 w-4" />
                      {t("chat.media.download")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleExternalView}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("chat.media.openExternally")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {children}
      </MessageContent>
    )
  },
)

MessageVideo.displayName = "MessageVideo"

async function generateWaveformData(url: string): Promise<number[]> {
  const cached = waveformCache.get(url)
  if (cached) return cached

  const audioContext = new (window.AudioContext ||
    (window as any).webkitAudioContext)()
  try {
    const response = await fetch(url)
    const arrayBuffer = await response.arrayBuffer()
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

    const channelData = audioBuffer.getChannelData(0)
    const samples = 60
    const blockSize = Math.floor(channelData.length / samples)
    const waveformData: number[] = []

    for (let i = 0; i < samples; i++) {
      const start = i * blockSize
      const end = start + blockSize
      let sum = 0
      for (let j = start; j < end; j++) {
        sum += Math.abs(channelData[j])
      }
      waveformData.push(sum / blockSize)
    }

    const max = Math.max(...waveformData)
    const normalized = waveformData.map((val) => val / max)
    waveformCache.set(url, normalized)
    return normalized
  } finally {
    audioContext.close()
  }
}

const MessageAudio = memo(
  forwardRef<HTMLDivElement, MessageAudioProps>(
    (
      {
        className,
        src,
        duration,
        autoPlay = false,
        onDownload,
        onExternalView,
        showDownload = true,
        forwarded = false,
        reply = false,
        children,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation()
      const audioRef = useRef<HTMLAudioElement>(null)
      const progressRef = useRef<HTMLDivElement>(null)
      const rafRef = useRef<number>(0)
      const [isPlaying, setIsPlaying] = useState(false)
      const [currentTime, setCurrentTime] = useState(0)
      const [audioDuration, setAudioDuration] = useState(duration || 0)
      const [isLoading, setIsLoading] = useState(true)
      const [hasError, setHasError] = useState(false)
      const [waveform, setWaveform] = useState<number[]>([])
      const [volume, setVolume] = useState(1)
      const [isMuted, setIsMuted] = useState(false)
      const [showWaveformView, setShowWaveformView] = useState(false)
      const [volumeOpen, setVolumeOpen] = useState(false)

      const message = useMessage({ forwarded, reply })
      const { actionButtonsVisible } = useMessageContext()
      const audioPlayback = useAudioPlayback()
      const audioPlaybackRef = useRef(audioPlayback)
      audioPlaybackRef.current = audioPlayback

      const transcript = props.transcript
        ? props.transcript
        : message.body !== message.url
          ? message.body
          : undefined

      const url = src ? src : message?.url
      const download = useHandleDownload()
      const downloadFileName = useDownloadFileName(message)

      // Generate waveform data lazily (only when waveform view is toggled on)
      useEffect(() => {
        if (!showWaveformView) return
        const waveformUrl = src ? src : message.url
        if (!waveformUrl) return

        // Check cache synchronously first
        const cached = waveformCache.get(waveformUrl)
        if (cached) {
          setWaveform(cached)
          return
        }

        let cancelled = false
        generateWaveformData(waveformUrl)
          .then((data) => {
            if (!cancelled) setWaveform(data)
          })
          .catch((error) => {
            if (!cancelled) {
              console.warn(
                "Waveform generation failed (CORS or network):",
                error,
              )
              setWaveform([])
              // On CORS failure, toggle back to progress bar view
              setShowWaveformView(false)
            }
          })

        return () => {
          cancelled = true
        }
      }, [showWaveformView, message.url, src])

      // Audio element event listeners (stable — no context dependency)
      useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const handleDurationChange = () => setAudioDuration(audio.duration)
        const handleCanPlay = () => setIsLoading(false)
        const handleError = () => {
          setIsLoading(false)
          setHasError(true)
        }

        const handlePlay = () => {
          setIsPlaying(true)
          audioPlaybackRef.current.play(message.id)
        }

        const handlePause = () => {
          setIsPlaying(false)
          audioPlaybackRef.current.stop()
          setCurrentTime(audio.currentTime)
        }

        const handleEnded = () => {
          setIsPlaying(false)
          audioPlaybackRef.current.stop()
          setCurrentTime(0)
        }

        audio.addEventListener("durationchange", handleDurationChange)
        audio.addEventListener("play", handlePlay)
        audio.addEventListener("pause", handlePause)
        audio.addEventListener("ended", handleEnded)
        audio.addEventListener("canplay", handleCanPlay)
        audio.addEventListener("error", handleError)

        return () => {
          audio.removeEventListener("durationchange", handleDurationChange)
          audio.removeEventListener("play", handlePlay)
          audio.removeEventListener("pause", handlePause)
          audio.removeEventListener("ended", handleEnded)
          audio.removeEventListener("canplay", handleCanPlay)
          audio.removeEventListener("error", handleError)
        }
      }, [message.id])

      // RAF-based progress updates — runs only while playing
      useEffect(() => {
        if (!isPlaying) return
        const audio = audioRef.current
        if (!audio) return

        const updateProgress = () => {
          if (audio && !audio.paused) {
            setCurrentTime(audio.currentTime)
            rafRef.current = requestAnimationFrame(updateProgress)
          }
        }
        rafRef.current = requestAnimationFrame(updateProgress)

        return () => cancelAnimationFrame(rafRef.current)
      }, [isPlaying])

      // Pause this audio when another message starts playing
      useEffect(() => {
        if (
          audioPlayback.playingMessageId !== null &&
          audioPlayback.playingMessageId !== message.id &&
          isPlaying
        ) {
          audioRef.current?.pause()
        }
      }, [audioPlayback.playingMessageId, message.id, isPlaying])

      const togglePlayPause = useCallback(() => {
        if (audioRef.current) {
          if (isPlaying) {
            audioRef.current.pause()
          } else {
            audioRef.current.play()
          }
        }
      }, [isPlaying])

      const handleSeek = useCallback(
        (value: number) => {
          if (!isKnownDuration(audioDuration)) return
          if (audioRef.current) {
            audioRef.current.currentTime = (value / 100) * audioDuration
          }
        },
        [audioDuration],
      )

      const handleVolumeChange = useCallback((values: number[]) => {
        if (audioRef.current) {
          const value = values[0]
          const newVolume = value / 100
          audioRef.current.volume = newVolume
          setVolume(newVolume)
          setIsMuted(newVolume === 0)
        }
      }, [])

      const toggleMute = useCallback(() => {
        if (audioRef.current) {
          const newMuted = !isMuted
          audioRef.current.muted = newMuted
          setIsMuted(newMuted)
        }
      }, [isMuted])

      const handleProgressClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
          if (!progressRef.current || !isKnownDuration(audioDuration)) return

          const rect = progressRef.current.getBoundingClientRect()
          const percent = ((e.clientX - rect.left) / rect.width) * 100
          handleSeek(Math.max(0, Math.min(100, percent)))
        },
        [audioDuration, handleSeek],
      )

      const handleWaveformClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
          if (!isKnownDuration(audioDuration)) return

          const rect = e.currentTarget.getBoundingClientRect()
          const percent = ((e.clientX - rect.left) / rect.width) * 100
          handleSeek(Math.max(0, Math.min(100, percent)))
        },
        [audioDuration, handleSeek],
      )

      const handleDownload = onDownload
        ? onDownload
        : downloadFileName && url
          ? () => {
              download(url, downloadFileName)
            }
          : undefined

      const handleExternalView = () => {
        if (onExternalView) {
          onExternalView()
        } else {
          window.open(url, "_blank")
        }
      }

      return (
        <MessageContent ref={ref} {...props}>
          <div
            className={cn(
              "flex w-full items-center rounded-lg border bg-secondary",
              // Mobile-first responsive sizing and height
              "h-8 px-1", // Mobile default - more compact
              "sm:h-9 sm:px-2", // Slightly larger on small screens and up
              "min-w-[250px]",
              "sm:min-w-[300px]", // Desktop width constraints
              // Shrink when action buttons are visible on mobile
              isMobile &&
                actionButtonsVisible &&
                "h-7 min-w-[200px] max-w-[280px]",
              className,
            )}
          >
            <audio ref={audioRef} src={url} autoPlay={autoPlay} />

            {/* Main Controls Row */}
            <div
              className={cn(
                "flex w-full items-center justify-center gap-1",
                "sm:gap-2", // More spacing on larger screens
                // More compact when action buttons are visible on mobile
                isMobile && actionButtonsVisible && "gap-0.5",
              )}
            >
              {/* Play/Pause Button */}
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "h-6 w-6 shadow-xs", // Mobile default - smaller
                  "sm:h-7 sm:w-7", // Larger on small screens and up
                  // Even smaller when action buttons are visible on mobile
                  isMobile && actionButtonsVisible && "h-5 w-5",
                )}
                onClick={togglePlayPause}
                disabled={hasError}
              >
                {isLoading ? (
                  <Loader2
                    className={cn(
                      "h-3 w-3 animate-spin", // Mobile default - smaller
                      "sm:h-4 sm:w-4", // Larger on small screens and up
                      isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                    )}
                  />
                ) : hasError ? (
                  <AlertCircle
                    className={cn(
                      "h-3 w-3", // Mobile default - smaller
                      "sm:h-4 sm:w-4", // Larger on small screens and up
                      isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                    )}
                  />
                ) : isPlaying ? (
                  <Pause
                    className={cn(
                      "h-3 w-3", // Mobile default - smaller
                      "sm:h-4 sm:w-4", // Larger on small screens and up
                      isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                    )}
                  />
                ) : (
                  <Play
                    className={cn(
                      "h-3 w-3", // Mobile default - smaller
                      "sm:h-4 sm:w-4", // Larger on small screens and up
                      isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                    )}
                  />
                )}
              </Button>

              {/* Progress Section */}
              <div
                className={cn(
                  "flex flex-1 items-center gap-1",
                  "sm:gap-2", // More spacing on larger screens
                  // More compact when action buttons are visible on mobile
                  isMobile && actionButtonsVisible && "gap-0.5",
                )}
              >
                <span
                  className={cn(
                    "font-mono text-xs text-accent-foreground",
                    // Hide time display on very small screens when actions are visible
                    isMobile && actionButtonsVisible && "hidden",
                  )}
                >
                  {formatTime(currentTime)}/{formatTime(audioDuration)}
                </span>

                {/* Waveform or Progress */}
                <div className="flex-1">
                  {showWaveformView && waveform.length > 0 ? (
                    <div
                      className={cn(
                        "flex cursor-pointer items-end justify-center gap-0.5",
                        "h-5 sm:h-6", // Responsive height
                        // Smaller when action buttons are visible on mobile
                        isMobile && actionButtonsVisible && "h-4 gap-px",
                      )}
                      onClick={handleWaveformClick}
                    >
                      {waveform.map((amplitude, index) => (
                        <div
                          key={index}
                          className={cn(
                            "rounded-sm bg-muted transition-colors",
                            "w-1 sm:w-1", // Responsive width
                            // Thinner when action buttons are visible on mobile
                            isMobile && actionButtonsVisible && "w-0.5",
                            index / waveform.length <=
                              playedFraction(currentTime, audioDuration)
                              ? "bg-primary"
                              : "bg-gray-200 dark:bg-gray-600",
                          )}
                          style={{
                            height: `${Math.max(
                              2,
                              amplitude *
                                (isMobile && actionButtonsVisible ? 16 : 24),
                            )}px`,
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <div
                      ref={progressRef}
                      className="cursor-pointer"
                      onClick={handleProgressClick}
                    >
                      <Progress
                        value={playedFraction(currentTime, audioDuration) * 100}
                        className="h-2 bg-base-primary/20 [&>div]:bg-base-primary"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Volume Control */}
              <Popover open={volumeOpen} onOpenChange={setVolumeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-6 w-6", // Mobile default - smaller
                      "sm:h-7 sm:w-7", // Larger on small screens and up
                      // Even smaller when action buttons are visible on mobile
                      isMobile && actionButtonsVisible && "h-5 w-5",
                    )}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX
                        className={cn(
                          "h-3 w-3", // Mobile default - smaller
                          "sm:h-4 sm:w-4", // Larger on small screens and up
                          isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                        )}
                      />
                    ) : (
                      <Volume2
                        className={cn(
                          "h-3 w-3", // Mobile default - smaller
                          "sm:h-4 sm:w-4", // Larger on small screens and up
                          isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                        )}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="h-auto w-auto p-3"
                  side="top"
                  align="center"
                >
                  <div className="flex w-6 flex-col items-center gap-2">
                    <div className="my-3 flex h-20 items-center justify-center">
                      <Slider
                        orientation="vertical"
                        onValueChange={handleVolumeChange}
                        value={isMuted ? [0] : [volume * 100]}
                        max={100}
                        step={1}
                      />
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-6 whitespace-nowrap px-2 text-xs",
                        isMuted && "bg-muted",
                      )}
                      onClick={toggleMute}
                    >
                      <VolumeX className="h-4 w-4" />
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      "h-6 w-6", // Mobile default - smaller
                      "sm:h-7 sm:w-7", // Larger on small screens and up
                      // Even smaller when action buttons are visible on mobile
                      isMobile && actionButtonsVisible && "h-5 w-5",
                    )}
                  >
                    <MoreVertical
                      className={cn(
                        "h-3 w-3", // Mobile default - smaller
                        "sm:h-4 sm:w-4", // Larger on small screens and up
                        isMobile && actionButtonsVisible && "h-2.5 w-2.5",
                      )}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {!isMobile && (
                    <DropdownMenuItem
                      onClick={() => setShowWaveformView(!showWaveformView)}
                    >
                      <AudioLines className="mr-2 h-4 w-4" />
                      {showWaveformView
                        ? t("chat.media.hideWaveform")
                        : t("chat.media.showWaveform")}
                    </DropdownMenuItem>
                  )}
                  {showDownload && (
                    <DropdownMenuItem onClick={handleDownload}>
                      <Download className="mr-2 h-4 w-4" />
                      {t("chat.media.download")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleExternalView}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    {t("chat.media.openExternally")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {transcript && <MessageText content={transcript} className="mt-2" />}

          {children}
        </MessageContent>
      )
    },
  ),
)

MessageAudio.displayName = "MessageAudio"

export { MessageVideo, MessageAudio }
